import { AnimatedContentLoader, BetaPill, Button } from '@root/components'
import AccountingRuleFormModal from './Form/AccountingRuleFormModal'
import { useInfiniteAccountingRules } from '@root/query/hooks'
import styled from 'styled-components'
import { FaPlus } from 'react-icons/fa'
import { collectFromPages } from '@root/query'
import AccountingRulesList from './AccountingRulesList'
import AccountingRuleDeleteModal from './AccountingRuleDeleteModal'
import { useTranslation } from 'react-i18next'
import AccountingRulesLander from './AccountingRulesLander'
import { useScreen } from '@root/utils'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { useOpenAccountingRuleFromQuery } from './useOpenAccountingRuleFromQuery'

const AccountingRuleSettings: React.FC = () => {
  const [t] = useTranslation()
  const { isMobile } = useScreen()
  const {
    isFormOpen,
    formRule,
    deleteRule,
    isDeleteFormOpen,
    onEditRule,
    onDeleteRule,
    onFormClose,
    onCreateRule
  } = useOpenAccountingRuleFromQuery()

  const { data, isLoading, hasNextPage, fetchNextPage } = useInfiniteAccountingRules()

  const accountingRules = collectFromPages(data)

  return (
    <SettingsSubPageLayout title={t('settings.sections.automationRules')}>
      <StyledPageContent>
        <AnimatedContentLoader isLoading={isLoading}>
          {accountingRules.length === 0 ? (
            <AccountingRulesLander onCreateRule={onCreateRule} />
          ) : (
            <>
              <StyledActionContainer $isMobile={isMobile}>
                <StyledHeaderContainer $isMobile={isMobile}>
                  <StyledRowWrapper>
                    <StyledHeader>{t('settings.automation.accountingRules.header')}</StyledHeader>
                    <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
                  </StyledRowWrapper>

                  <StyledHelperText>
                    {t('settings.automation.accountingRules.headerHelper')}
                  </StyledHelperText>
                </StyledHeaderContainer>

                <Button onClick={onCreateRule} intent="success" icon={<FaPlus />}>
                  {t('settings.automation.accountingRules.new')}
                </Button>
              </StyledActionContainer>

              <StyledRulesWrapper>
                <AccountingRulesList
                  accountingRules={accountingRules}
                  onScrollBottom={() => {
                    if (hasNextPage) {
                      fetchNextPage()
                    }
                  }}
                  onEditRule={onEditRule}
                  onDeleteRule={onDeleteRule}
                />
              </StyledRulesWrapper>
            </>
          )}
        </AnimatedContentLoader>

        <AccountingRuleFormModal
          isVisible={isFormOpen}
          rule={formRule}
          handleOnClose={onFormClose}
        />

        <AccountingRuleDeleteModal
          rule={deleteRule}
          isVisible={isDeleteFormOpen}
          handleOnClose={onFormClose}
        />
      </StyledPageContent>
    </SettingsSubPageLayout>
  )
}

export default AccountingRuleSettings

const StyledHeaderContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  width: 75%;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    width: 100%;
  `}
`

const StyledHeader = styled.h3`
  margin: 0;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.neutralBlack};
`
const StyledHelperText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const StyledActionContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    flex-direction: row-reverse;
    flex-wrap: wrap;
  `}
`
const StyledPageContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const StyledRulesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacing.xl}rem;
  flex: 1;
  min-height: 0;
  max-height: 800px;
  overflow: hidden;
`

const StyledRowWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
